import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clamp } from "lodash";
import Input from "components/form/input/Input";
import "./websiteBuilderRange.scss";
import { isWholeNumber } from "helpers/Helpers";
import classNames from "classnames";

interface IWebsiteBuilderRangeProps {
  label?: string | JSX.Element;
  min: number;
  max: number;
  step: number;
  value: number;
  setValue: (updatedValue: number) => void;
  mixed?: boolean;
  /** Shown as the value input text when `mixed` is true and no single value applies. */
  mixedLabel?: string;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

interface IWebsiteBuilderRangeState {
  dragging: boolean;
  draggingMouseOffset: number;
  dragSliderWidth: number;
  dragSliderStops: number[];
  valueInput: string;
}

const HANDLE_DIMENSIONS = 24;
const HALF_HANDLE_DIMENSIONS = HANDLE_DIMENSIONS / 2;

export function WebsiteBuilderRange(props: IWebsiteBuilderRangeProps) {
  const {
    label,
    min,
    max,
    step,
    value,
    setValue,
    mixed = false,
    mixedLabel,
    onGestureStart,
    onGestureEnd,
  } = props;

  const average = (min + max) / 2;

  const sliderRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const onGestureStartRef = useRef(onGestureStart);
  const onGestureEndRef = useRef(onGestureEnd);
  const gestureActiveRef = useRef(false);
  const trackPointerIdRef = useRef<number | null>(null);
  const endGestureRef = useRef<() => void>(() => undefined);

  onGestureStartRef.current = onGestureStart;
  onGestureEndRef.current = onGestureEnd;

  const [state, setState] = useState<IWebsiteBuilderRangeState>({
    dragging: false,
    draggingMouseOffset: null,
    dragSliderWidth: null,
    dragSliderStops: null,
    valueInput: value == null ? "" : String(value),
  });

  function startGesture() {
    if (gestureActiveRef.current) {
      return;
    }

    gestureActiveRef.current = true;
    onGestureStartRef.current?.();
  }

  function endGesture() {
    if (!gestureActiveRef.current) {
      return;
    }

    gestureActiveRef.current = false;
    onGestureEndRef.current?.();
  }

  endGestureRef.current = endGesture;

  function isStepAligned(num: number): boolean {
    const steps = Math.round((num - min) / step);
    const aligned = min + steps * step;
    return Math.abs(aligned - num) < 0.001;
  }

  function isValueInputValid(valueInput: string) {
    if (valueInput.trim() === "") {
      return false;
    }

    const parsed = Number(valueInput);
    if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
      return false;
    }

    if (Number.isInteger(step)) {
      return isWholeNumber(valueInput);
    }

    return isStepAligned(parsed);
  }

  const mixedEditing =
    mixed &&
    mixedLabel != null &&
    (state.valueInput === mixedLabel || state.valueInput.trim() === "");
  const valueInputValid = mixedEditing || isValueInputValid(state.valueInput);

  const sliderValue = mixedEditing
    ? min
    : isValueInputValid(state.valueInput)
      ? Number(state.valueInput)
      : value;

  useEffect(() => {
    if (mixed && mixedLabel) {
      setState(prev => ({
        ...prev,
        valueInput: mixedLabel,
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      valueInput: value == null ? "" : String(value),
    }));
  }, [value, mixed, mixedLabel]);

  useEffect(() => {
    if (!sliderRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      let updatedDragSliderWidth = sliderRef.current?.getBoundingClientRect()?.width;

      if (updatedDragSliderWidth == null) {
        setState(prev => ({ ...prev, dragSliderWidth: null, dragSliderStops: null }));
      } else {
        updatedDragSliderWidth -= HANDLE_DIMENSIONS;

        const updatedDragSliderStops: number[] = [];

        const stepPercent = step / (max - min);
        const adjustmentStep = stepPercent * updatedDragSliderWidth;

        let adjustment = 0;
        let adjustmentIndex = 0;

        while (updatedDragSliderWidth - adjustment > 0.01) {
          adjustment = adjustmentIndex * adjustmentStep;
          updatedDragSliderStops.push(adjustment);
          adjustmentIndex++;
        }

        setState(prev => ({
          ...prev,
          dragSliderWidth: updatedDragSliderWidth,
          dragSliderStops: updatedDragSliderStops,
        }));
      }
    });

    resizeObserver.observe(sliderRef.current);
    return () => resizeObserver.disconnect();
  }, [step, max, min]);

  const handleX = useMemo(() => {
    if (state.dragSliderWidth != null && sliderValue != null) {
      const valuePercent = (sliderValue - min) / (max - min);
      const updatedHandleX = valuePercent * state.dragSliderWidth;

      return updatedHandleX;
    } else {
      return 0;
    }
  }, [state.dragSliderWidth, sliderValue, min, max]);

  function calculateValue(
    clientX: number,
    offset: number,
    dragSliderStops: number[],
    min: number,
    max: number,
    step: number,
  ) {
    const sliderX = sliderRef.current?.getBoundingClientRect()?.x;

    if (sliderX == null || dragSliderStops == null || dragSliderStops.length === 0) {
      setValue(min);

      setState(prev => ({
        ...prev,
        valueInput: String(min),
      }));
    }

    const handleX = clientX - offset - sliderX;

    let lowestDelta: number = null;
    let lowestDeltaIndex: number = 0;

    for (let i = 0; i < dragSliderStops.length; i++) {
      const delta = Math.abs(dragSliderStops[i] - handleX);

      if (lowestDelta == null || delta < lowestDelta) {
        lowestDelta = delta;
        lowestDeltaIndex = i;
      }
    }

    const updatedValue = clamp(min + lowestDeltaIndex * step, min, max);

    setValue(updatedValue);

    setState(prev => ({
      ...prev,
      valueInput: String(updatedValue),
    }));
  }

  const handlePointerMove = useCallback(
    (e: MouseEvent) => {
      calculateValue(e.clientX, state.draggingMouseOffset, state.dragSliderStops, min, max, step);
    },
    [state.draggingMouseOffset, state.dragSliderStops, min, max, step],
  );

  const handleTrackPointerUp = useCallback((event: PointerEvent) => {
    if (trackPointerIdRef.current !== null && event.pointerId !== trackPointerIdRef.current) {
      return;
    }

    window.removeEventListener("pointerup", handleTrackPointerUp);
    window.removeEventListener("pointercancel", handleTrackPointerUp);
    trackPointerIdRef.current = null;
    endGestureRef.current();
  }, []);

  const handlePointerUp = useCallback((e: PointerEvent) => {
    handleRef.current?.releasePointerCapture(e.pointerId);

    setState(prev => ({ ...prev, dragging: false, draggingMouseOffset: null }));
    endGestureRef.current();
  }, []);

  useEffect(() => {
    if (state.dragging) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [state.dragging, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    return () => {
      window.removeEventListener("pointerup", handleTrackPointerUp);
      window.removeEventListener("pointercancel", handleTrackPointerUp);
      trackPointerIdRef.current = null;
      endGestureRef.current();
    };
  }, [handleTrackPointerUp]);

  function calculateSliderGradient() {
    const blue = "#3D5FA0";
    const grey = "#EAECF0";
    const progressX = handleX + HALF_HANDLE_DIMENSIONS;

    return `linear-gradient(
        to right,
        ${blue} 0%,
        ${blue} ${progressX}px,
        ${grey} ${progressX}px,
        ${grey} 100%
      )`;
  }

  return (
    <div className="website-builder-range">
      {label != null ? <div className="range-label">{label}</div> : null}
      <div className="range-content">
        <div
          ref={sliderRef}
          className="range-slider"
          style={{
            background: calculateSliderGradient(),
          }}
          onPointerDown={e => {
            if (e.button !== 0) {
              return;
            }

            startGesture();
            trackPointerIdRef.current = e.pointerId;
            window.addEventListener("pointerup", handleTrackPointerUp);
            window.addEventListener("pointercancel", handleTrackPointerUp);
            calculateValue(
              e.clientX,
              HALF_HANDLE_DIMENSIONS,
              state.dragSliderStops,
              min,
              max,
              step,
            );
          }}
        >
          <div
            ref={handleRef}
            className="range-slider-handle"
            style={{
              left: handleX,
              width: HANDLE_DIMENSIONS,
              height: HANDLE_DIMENSIONS,
              cursor: state.dragging ? "grabbing" : "grab",
            }}
            onPointerDown={e => {
              e.stopPropagation();

              const ref = handleRef.current;

              if (e.button === 0 && ref) {
                ref.setPointerCapture(e.pointerId);
                startGesture();

                setState(prev => ({
                  ...prev,
                  dragging: true,
                  draggingMouseOffset: e.clientX - ref.getBoundingClientRect().x,
                }));
              }
            }}
          />
        </div>
        <Input
          className={classNames("range-value-input", {
            "range-value-input-large": average >= 10000,
            "range-value-input-medium": average < 10000,
            "range-value-input-small": average < 1000,
            "range-value-input--mixed": mixed && mixedLabel != null,
          })}
          labelHidden
          value={state.valueInput}
          error={!valueInputValid}
          onFocus={() => {
            startGesture();
            if (mixed && mixedLabel) {
              setState(prev => {
                if (prev.valueInput === mixedLabel) {
                  return { ...prev, valueInput: "" };
                }
                return prev;
              });
            }
          }}
          onBlur={() => {
            endGesture();
            if (mixed && mixedLabel) {
              setState(prev => {
                if (!isValueInputValid(prev.valueInput)) {
                  return { ...prev, valueInput: mixedLabel };
                }
                return prev;
              });
            }
          }}
          onChange={e => {
            const updatedValueInput = e.target.value;

            setState(prev => ({
              ...prev,
              valueInput: updatedValueInput,
            }));

            if (isValueInputValid(updatedValueInput)) {
              setValue(Number(updatedValueInput));
            }
          }}
        />
      </div>
    </div>
  );
}
